import { useCallback, useSyncExternalStore } from 'react';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { RegistrationStoreState } from '~/domain/readmodels/RegistrationReadModel';
import { getRegistrationStore } from '~/domain/ServiceContainer';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';

/**
 * Pure read-only subscription to the registration store.
 *
 * Returns a reactive, immutable view model of registrations.
 * Store context (patient, primaryImage) is set by
 * useRegistrationHandlers at the app root — consumers
 * never need to resolve or set context.
 *
 * Uses useSyncExternalStore to bridge the mutable domain store
 * with React rendering.
 */
export function useRegistrationState(): RegistrationStoreState {
    const store = getRegistrationStore();
    return useSyncExternalStore(
        store.subscribe,
        store.getSnapshot,
        store.getSnapshot
    );
}

/**
 * Hook that returns a stable callback to select a registration
 * by SOP Instance UID. Pass undefined to clear selection.
 */
export function useSelectRegistration(): (
    sopInstanceUid: string | undefined,
    imageSeriesUid?: string
) => void {
    const store = getRegistrationStore();
    return useCallback(
        (sopInstanceUid: string | undefined, imageSeriesUid?: string) => {
            store.selectRegistration(sopInstanceUid, imageSeriesUid);
        },
        [store]
    );
}

/**
 * Hook that returns the live domain object for the currently
 * selected registration.
 *
 * Use only when the mutable domain
 * object is required (e.g. viewport/engine operations,
 * accessing transformation matrix).
 *
 * For UI rendering, prefer the view model's selectedRegistration
 * which is an immutable projection.
 */
export function useSelectedRegistrationObject(): RigidRegistration | undefined {
    const viewModel = useRegistrationState();
    const store = getRegistrationStore();
    if (!viewModel.selectedSopInstanceUid) {
        return undefined;
    }
    return store.getSelectedRegistration();
}

/**
 * Hook that returns a callback to activate a registration by its
 * SOP Instance UID and image series UID. Resolves domain objects
 * from the store, sets the secondary image in the viewport.
 */
export function useActivateRegistration(): (
    registrationSopUid: string,
    imageSeriesUid: string
) => void {
    const store = getRegistrationStore();
    const { setEnvironmentSecondaryImage } = useViewportEnvironment();

    return useCallback(
        (registrationSopUid: string, imageSeriesUid: string) => {
            const reg = store.getRegistrationBySopUid(registrationSopUid);
            const img = store.getImageBySeriesUid(imageSeriesUid);
            if (!reg || !img) {
                return;
            }
            store.selectRegistration(registrationSopUid, imageSeriesUid);
            setEnvironmentSecondaryImage(img, reg);
        },
        [store, setEnvironmentSecondaryImage]
    );
}
